import React, { useState, useEffect } from "react";
import axios from "axios";
import jwt_decode from "jwt-decode";
import Navbar from "../Components/Navbar";
import HelpMessage from "../Components/HelpMessage";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import { toast } from "react-toastify";

import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs";

const getStoredSupplierCode = () => {
  const stored = localStorage.getItem("currentUser");
  return stored ? stored.trim() : "";
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [docWidth, setDocWidth] = useState(Math.min(window.innerWidth * 0.85, 900));
  const [loading, setLoading] = useState(true);
  const [supplierCode, setSupplierCode] = useState(getStoredSupplierCode());
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwt_decode(token);
        const userRole = decoded?.role || "";
        setIsAdmin(userRole.toLowerCase().includes("admin"));
        setUserId(decoded?.user || null);
      } catch (error) {
        setIsAdmin(false);
        setUserId(null);
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setDocWidth(Math.min(window.innerWidth * 0.85, 900));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const loadDocuments = async () => {
      // Use supplierCode if available, otherwise fall back to userId
      const supplierForContext = supplierCode || userId;
      if (!supplierForContext) {
        setLoading(false);
        setDocuments([]);
        return;
      }
      try {
        setLoading(true);
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { supplier: supplierForContext }
        };
        // Always send supplier to backend so it can determine the company
        // Backend returns all company documents (company-wide, not supplier-specific)
        const { data } = await axios.get(`${process.env.REACT_APP_API_BASEURL}/api/documents`, config);
        setDocuments(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error loading documents:", error);
        toast.error("Unable to load documents");
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [supplierCode, userId, isAdmin]);

  useEffect(() => {
    const handleSupplierChange = (event) => {
      const next = event?.detail?.supplier?.trim() || getStoredSupplierCode();
      setSupplierCode(next);
      setSelectedDoc(null);
    };

    window.addEventListener("supplierContextChanged", handleSupplierChange);
    return () => {
      window.removeEventListener("supplierContextChanged", handleSupplierChange);
    };
  }, []);

  const buildDocumentUrl = (relativePath = "") =>
    `${process.env.REACT_APP_API_BASEURL}/assets/documents/other/${relativePath}`;

  const handleSelectDoc = (doc) => {
    const relativePath = doc?.RelativePath?.trim();
    if (!relativePath) {
      return;
    }

    const mimeType = doc?.MimeType || "application/octet-stream";
    const viewerType = mimeType.startsWith("application/pdf")
      ? "pdf"
      : mimeType.startsWith("image/")
        ? "image"
        : "download";

    setSelectedDoc({
      ...doc,
      url: buildDocumentUrl(relativePath),
      viewerType,
      mimeType,
    });
    setNumPages(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    handleUpload(file);
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const supplierForContext = supplierCode || userId;
    if (!supplierForContext) {
      toast.error("No supplier selected");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("supplier", supplierForContext);

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      };

      await axios.post(`${process.env.REACT_APP_API_BASEURL}/api/documents/upload`, formData, config);

      toast.success("Document uploaded successfully");
      
      // Reload documents list
      const loadConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: { supplier: supplierForContext }
      };
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASEURL}/api/documents`, loadConfig);
      setDocuments(Array.isArray(data) ? data : []);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.response?.data?.message || "Unable to upload document");
    } finally {
      setUploading(false);
    }
  };

  const heroTitle = selectedDoc ? selectedDoc.DocName : "Documents";

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main">
        {selectedDoc ? (
          <section className="documents-viewer card">
            <header className="card-header">
              <div className="card-header--stacked">
                <h1 className="page-hero__title">{heroTitle}</h1>
              </div>
              <button className="button ghost sm" onClick={() => setSelectedDoc(null)}>
                <i className="uicon-arrow-narrow-left" /> Back to list
              </button>
            </header>
            <div className="documents-viewer__canvas">
              {selectedDoc.viewerType === "pdf" ? (
                <Document
                  file={selectedDoc.url}
                  onLoadSuccess={({ numPages: nextNumPages }) => setNumPages(nextNumPages)}
                >
                  {Array.from({ length: numPages || 0 }, (_, index) => (
                    <Page key={`page_${index + 1}`} pageNumber={index + 1} width={docWidth} />
                  ))}
                </Document>
              ) : selectedDoc.viewerType === "image" ? (
                <div className="documents-image">
                  <img src={selectedDoc.url} alt={selectedDoc.DocName} />
                </div>
              ) : (
                <div className="documents-download">
                  <p>This document type can’t be previewed.</p>
                  <a href={selectedDoc.url} target="_blank" rel="noopener noreferrer" className="button">
                    Download
                  </a>
                </div>
              )}
            </div>
          </section>
        ) : (
          <section className="documents-list card">
            <header className="card-header">
              <h1 className="page-hero__title">{heroTitle}</h1>
              {isAdmin && (
                <div className="card-header__actions">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                  <button
                    className="button primary sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload Document"}
                  </button>
                </div>
              )}
            </header>
            {loading ? (
              <div className="notes-empty">Loading documents…</div>
            ) : documents.length === 0 ? (
              <div className="notes-empty">No documents published yet.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th scope="col">Document</th>
                    <th scope="col">Published</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={`${doc.RelativePath}`} onClick={() => handleSelectDoc(doc)}>
                      <td>{doc.DocName}</td>
                      <td>{doc.FullDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        <div className="home-help">
          <HelpMessage />
        </div>
      </main>
    </div>
  );
};

export default Documents;
